import { T } from '../theme'
import { formatSeasonShort } from '../context/SeasonContext'

/**
 * PlayerCardPreview — apercu LIVE de la carte joueur V2 (carte V24).
 *
 * Replique React fidele (pas pixel-perfect) de app/templates/
 * player_card_v2.html.j2 cote backend, qui reste la source de verite
 * visuelle. Seuls le club (nom, couleurs, logo) et le niveau de
 * championnat sont dynamiques : le joueur est fige (Mehdi Jouani) car le
 * coach ne le modifie pas dans le contexte ClubSettings.
 *
 * Props : { primaryColor, secondaryColor, competitionLevel, clubName,
 *           logoUrl }
 */

const INK = '#0a0a08'
const INK2 = '#1a1916'
const CREAM = '#f5f2eb'
const GOLD = '#4F8EF7'
const GOLD_BRIGHT = '#f5d76e'

function clampHex(input, fallback) {
  const s = String(input || '').trim()
  const m = /^#?([0-9a-fA-F]{6})$/.exec(s)
  return m ? `#${m[1].toLowerCase()}` : fallback
}
function hexToRgb(hex) {
  const h = hex.replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}
function soft(hex, a = 0.16) {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r},${g},${b},${a})`
}
function bright(hex) {
  // Eclaircit vers le blanc (~38 %) — proxy de club_*_bright backend.
  const [r, g, b] = hexToRgb(hex)
  const mix = (c) => Math.round(c + (255 - c) * 0.38)
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`
}

export default function PlayerCardPreview({
  primaryColor,
  secondaryColor,
  competitionLevel,
  clubName,
  logoUrl,
  photoUrl,
  season,
}) {
  const cP = clampHex(primaryColor, GOLD)
  const cS = clampHex(secondaryColor, '#0f0f0d')
  const cPb = bright(cP)
  const cPs = soft(cP)
  const cSs = soft(cS)

  const club = (clubName || 'MON CLUB').trim()
  const level = (competitionLevel || 'CHAMPIONNAT').trim()

  const W = 380
  const H = Math.round((W * 16) / 9) // 9:16

  return (
    <div
      style={{
        width: W,
        height: H,
        position: 'relative',
        borderRadius: 24,
        fontFamily: T.body,
        color: CREAM,
        overflow: 'hidden',
        background: `radial-gradient(ellipse at 50% 0%, ${cPs} 0%, transparent 50%), radial-gradient(ellipse at 50% 100%, ${cSs} 0%, transparent 50%), linear-gradient(180deg, #1a1916 0%, #0a0a08 100%)`,
        // k8 : contours renforcés — anneau couleur club 3px, anneau gold
        // épaissi + halo gold extérieur pour démarquer la carte de son fond.
        boxShadow: `0 0 0 3px ${cP}, 0 0 0 5px ${INK}, 0 0 0 8px ${GOLD}, 0 0 0 9px rgba(79,142,247,0.35), 0 20px 60px -10px rgba(0,0,0,0.85), 0 0 90px -16px ${cP}`,
      }}
    >
      {/* Watermark logo */}
      {logoUrl && (
        <img
          src={logoUrl}
          alt=""
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)', width: '70%', height: 'auto',
            opacity: 0.06, pointerEvents: 'none', zIndex: 1,
          }}
        />
      )}
      {/* Shine */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 6, pointerEvents: 'none',
        borderRadius: 24,
        background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.07) 50%, transparent 70%)',
      }} />

      <div style={{
        position: 'relative', zIndex: 5, height: '100%',
        padding: '18px 20px 16px', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header club */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {logoUrl ? (
              <img src={logoUrl} alt={club} style={{ width: 44, height: 44, objectFit: 'contain', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))', flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 44, height: 44, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: T.display, fontSize: 20, color: INK,
                background: `linear-gradient(135deg, ${cP} 0%, ${cPb} 100%)`,
                borderRadius: '50%',
              }}>
                {club.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <div style={{
                fontFamily: T.display, fontSize: 14, color: CREAM, letterSpacing: 1.5,
                lineHeight: 1, textTransform: 'uppercase', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {club}
              </div>
              <div style={{ fontFamily: T.mono, fontSize: 8, color: cPb, letterSpacing: 1.5 }}>
                SAISON {season}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <span style={{ fontFamily: T.mono, fontSize: 7, color: 'rgba(245,242,235,0.5)', letterSpacing: 2 }}>SAISON</span>
            <span style={{ fontFamily: T.mono, fontSize: 11, color: 'rgba(245,242,235,0.5)', letterSpacing: 1.5, fontWeight: 700, lineHeight: 1 }}>{formatSeasonShort(season)}</span>
          </div>
        </div>

        {/* Top : note + position | photo */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 12, marginBottom: 10, alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10 }}>
            <div style={{ fontFamily: T.display, fontSize: 26, color: CREAM, letterSpacing: 2, lineHeight: 1 }}>
              ATTAQUANT
            </div>
            <div style={{ fontFamily: T.display, fontSize: 22, opacity: 0.85, lineHeight: 1, color: CREAM, letterSpacing: 2 }}>
              <span style={{ fontSize: 13, opacity: 0.7, marginRight: 2 }}>N°</span>9
            </div>
          </div>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%', padding: 3,
              background: `conic-gradient(from 0deg, ${cP} 0%, ${GOLD_BRIGHT} 25%, ${cS} 50%, ${GOLD_BRIGHT} 75%, ${cP} 100%)`,
            }} />
            <div style={{
              position: 'relative', width: '100%', height: '100%', borderRadius: '50%',
              overflow: 'hidden', background: INK,
              boxShadow: `0 0 30px ${cPs}, inset 0 0 0 2px ${INK2}`,
            }}>
              {photoUrl ? (
                <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                  <svg viewBox="0 0 100 110" style={{ width: '82%', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="32" r="19" fill={GOLD} />
                    <path d="M36,54 Q22,66 18,110 L82,110 Q78,66 64,54 Q58,49 50,48 Q42,49 36,54Z" fill={GOLD} />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tag */}
        <div style={{
          margin: '0 auto 10px', padding: '6px 16px', alignSelf: 'center',
          background: `linear-gradient(135deg, ${cPs} 0%, ${cSs} 100%)`,
          border: `1px solid ${GOLD}`, borderRadius: 100,
          fontFamily: T.display, fontSize: 13, color: GOLD_BRIGHT, letterSpacing: 2,
          display: 'inline-flex', alignItems: 'center', gap: 8,
          boxShadow: `0 0 20px ${cPs}`,
        }}>
          <span style={{ fontSize: 16 }}>⚔️</span><span>GUERRIER</span>
        </div>

        {/* Perf stats */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 10,
          padding: '8px', background: 'rgba(10,10,8,0.4)',
          borderTop: '1px solid rgba(79,142,247,0.2)', borderBottom: '1px solid rgba(79,142,247,0.2)',
        }}>
          {[
            { icon: '⚽', value: '2', label: 'BUTS' },
            { icon: '🎯', value: '1', label: 'PASSE D' },
            { icon: '⏱️', value: "90'", label: 'MINUTES' },
          ].map((s) => (
            <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 50 }}>
              <div style={{ fontSize: 16, lineHeight: 1, marginBottom: 2 }}>{s.icon}</div>
              <div style={{ fontFamily: T.display, fontSize: 22, color: GOLD_BRIGHT, lineHeight: 1, letterSpacing: -1 }}>{s.value}</div>
              <div style={{ fontFamily: T.mono, fontSize: 7, color: 'rgba(245,242,235,0.6)', letterSpacing: 1.5 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Nom */}
        <div style={{ fontFamily: T.display, fontSize: 34, color: CREAM, textAlign: 'center', letterSpacing: 2, lineHeight: 1, marginBottom: 12 }}>
          MICHAEL<br />O.
        </div>

        {/* Match block */}
        <div style={{
          textAlign: 'center', marginTop: 'auto', padding: '12px 14px',
          background: 'rgba(10,10,8,0.4)', border: '1px solid rgba(79,142,247,0.2)',
          borderRadius: 12, position: 'relative',
        }}>
          <div style={{
            position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
            background: `linear-gradient(135deg, ${INK2} 0%, ${INK} 100%)`,
            padding: '4px 14px', fontFamily: T.display, fontSize: 11, color: GOLD_BRIGHT,
            letterSpacing: 2.5, border: `1px solid ${GOLD}`, borderRadius: 100,
            whiteSpace: 'nowrap', textTransform: 'uppercase',
            boxShadow: '0 2px 8px rgba(0,0,0,0.6), 0 0 12px rgba(79,142,247,0.25)',
          }}>
            {level}
          </div>
          <div style={{
            fontFamily: T.display, letterSpacing: 1, display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: 24,
            color: CREAM, marginTop: 6,
          }}>
            <div style={{ flex: 1, fontSize: 20, lineHeight: 1.05, textAlign: 'center' }}>GFCA</div>
            <div style={{ flex: '0 0 auto', color: GOLD_BRIGHT, fontSize: 32, fontWeight: 900, whiteSpace: 'nowrap', padding: '0 4px' }}>5 – 0</div>
            <div style={{ flex: 1, fontSize: 20, lineHeight: 1.05, textAlign: 'center' }}>JSC</div>
          </div>
          <div style={{ marginTop: 8, fontFamily: T.mono, fontSize: 9, color: 'rgba(245,242,235,0.6)', letterSpacing: 1.5 }}>
            12 MAI 2026
          </div>
        </div>

        {/* Footer Insightball */}
        <div style={{ paddingTop: 10, marginTop: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{
            display: 'flex', alignItems: 'center', fontFamily: T.display,
            fontSize: 22, letterSpacing: 2, lineHeight: 1,
            filter: 'drop-shadow(0 2px 8px rgba(79,142,247,0.3))',
          }}>
            <span style={{ color: CREAM }}>INSIGHT</span><span style={{ color: GOLD }}>BALL</span>
          </div>
        </div>
      </div>
    </div>
  )
}
